import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import {
  CoworkOverlayName,
  CoworkPresetInfo,
  OverlayConfig,
  OverlayElements,
  OverlaySound,
  OverlayStyle,
  OverlaysPayload,
  SoundId,
} from '../../types/cowork';
import { playCoworkSound } from './coworkSounds';
import {
  Button,
  ErrorText,
  Input,
  Label,
  Muted,
  Select,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  TabBar,
  TabBtn,
  Toast,
  Toolbar,
} from '../ui';

const OVERLAY_LABELS: Record<CoworkOverlayName, string> = {
  timer: 'Timer',
  focus: 'Focus',
  tasks: 'Tareas de Euge',
  community: 'Comunidad',
  full: 'Overlay completo',
};

const Editor = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: 0.85rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: calc(100vh - 260px);
  overflow: auto;
  padding-right: 0.35rem;
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: 1fr 108px;
  gap: 0.5rem;
  align-items: center;
`;

const Section = styled.div`
  border-top: 1px solid rgba(255, 20, 147, 0.12);
  padding-top: 0.6rem;
  margin-top: 0.35rem;

  &:first-of-type { border-top: none; margin-top: 0; padding-top: 0; }
`;

const SectionTitle = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${theme.colors.pinkSoft};
  font-weight: 700;
  margin-bottom: 0.45rem;
`;

const Range = styled.input`
  width: 100%;
  accent-color: ${theme.colors.pink};
`;

const ColorInput = styled.input`
  width: 100%;
  height: 30px;
  padding: 2px;
  border-radius: 7px;
  border: 1px solid rgba(255, 20, 147, 0.22);
  background: rgba(10, 7, 22, 0.65);
  cursor: pointer;
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: ${theme.colors.textMuted};
  cursor: pointer;

  input { accent-color: ${theme.colors.pink}; width: 15px; height: 15px; }
`;

const Canvas = styled.div<{ $bg: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 20, 147, 0.2);
  background: ${({ $bg }) =>
    $bg === 'light' ? '#e8e8ec' :
    $bg === 'dark' ? '#101014' :
    $bg === 'scene' ? 'linear-gradient(135deg, #2a1d3d 0%, #123243 55%, #3d2a1d 100%)' :
    'repeating-conic-gradient(#2a2a2f 0% 25%, #1c1c20 0% 50%) 50% / 22px 22px'};

  iframe {
    position: absolute;
    inset: 0;
    width: 1920px;
    height: 1080px;
    transform-origin: top left;
    border: 0;
    background: transparent;
  }
`;

const UrlBox = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  input {
    font-family: 'Consolas', monospace;
    font-size: 0.78rem;
  }
`;

/* ------------------------------------------------------------------ */

function resolvePresetStyle(presets: CoworkPresetInfo[], presetId: string): Partial<OverlayStyle> {
  const preset = presets.find((p) => p.id === presetId);
  if (!preset) return {};
  if (preset.builtIn) return preset.style;

  const base = presets.find((p) => p.id === (preset.basePreset || 'eugenial'));
  return { ...(base?.style || {}), ...preset.style };
}

function diffStyle(draft: OverlayStyle, base: Partial<OverlayStyle>): Partial<OverlayStyle> {
  const out: Partial<OverlayStyle> = {};
  Object.entries(draft).forEach(([key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify((base as any)[key])) {
      (out as any)[key] = value;
    }
  });
  return out;
}

interface ElementControl {
  key: string;
  label: string;
  type: 'bool' | 'number' | 'text' | 'select';
  options?: { value: string; label: string }[];
  hint?: string;
}

const SLIDE_CONTROLS: ElementControl[] = [
  {
    key: 'autoScroll',
    label: 'Modo slide (de abajo hacia arriba)',
    type: 'bool',
    hint: 'Con el slide activo se muestra todo y se ignoran los máximos de arriba.',
  },
  {
    key: 'maxHeight',
    label: 'Alto de la ventana (px)',
    type: 'number',
    hint: 'La lista nunca pasa de este alto. Si no entra, empieza a desplazarse.',
  },
  { key: 'scrollSpeed', label: 'Velocidad del slide (px/seg.)', type: 'number' },
];

const ELEMENT_CONTROLS: Record<CoworkOverlayName, ElementControl[]> = {
  timer: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showPhaseName', label: 'Mostrar nombre de fase', type: 'bool' },
    { key: 'showSeconds', label: 'Mostrar segundos', type: 'bool' },
    { key: 'showConfig', label: 'Mostrar configuración (50/10)', type: 'bool' },
    { key: 'showCycle', label: 'Mostrar ciclo actual', type: 'bool' },
    { key: 'showProgressBar', label: 'Barra de progreso', type: 'bool' },
    { key: 'showFocusTask', label: 'Mostrar tarea de Euge', type: 'bool' },
  ],
  focus: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showTitle', label: 'Mostrar título', type: 'bool' },
    { key: 'title', label: 'Título', type: 'text' },
    { key: 'showTimer', label: 'Mostrar tiempo restante', type: 'bool' },
    { key: 'showPhaseName', label: 'Mostrar fase', type: 'bool' },
    { key: 'showSeconds', label: 'Mostrar segundos', type: 'bool' },
  ],
  tasks: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showTitle', label: 'Mostrar título', type: 'bool' },
    { key: 'title', label: 'Título', type: 'text' },
    { key: 'maxItems', label: 'Máximo visible', type: 'number' },
    { key: 'showCompleted', label: 'Mostrar completadas', type: 'bool' },
    { key: 'highlightFocus', label: 'Destacar la tarea en foco', type: 'bool' },
    { key: 'showCounter', label: 'Mostrar contador', type: 'bool' },
    ...SLIDE_CONTROLS,
  ],
  community: [
    { key: 'width', label: 'Ancho del panel (px)', type: 'number', hint: '0 = se adapta al contenido.' },
    { key: 'showTitle', label: 'Mostrar título', type: 'bool' },
    { key: 'title', label: 'Título', type: 'text' },
    {
      key: 'mode',
      label: 'Formato',
      type: 'select',
      options: [
        { value: 'expanded', label: 'Todas las tareas de cada uno' },
        { value: 'compact', label: 'Una línea por persona' },
      ],
    },
    { key: 'maxUsers', label: 'Máximo de usuarios', type: 'number' },
    {
      key: 'maxTasksPerUser',
      label: 'Máx. tareas por persona',
      type: 'number',
      hint: 'Solo cuántas se ven acá. El límite de lo que cada viewer puede cargar por chat está en Configuración → Límites.',
    },
    { key: 'showCompletedTasks', label: 'Incluir tareas completadas', type: 'bool' },
    {
      key: 'showTaskNumbers',
      label: 'Mostrar número de tarea',
      type: 'bool',
      hint: 'El número que el viewer usa en !listo y !deshacer. No cambia cuando completa otras tareas.',
    },
    { key: 'showUserCounts', label: 'Mostrar contador por persona', type: 'bool' },
    { key: 'onlyFocused', label: 'Solo usuarios con focus', type: 'bool' },
    { key: 'showCompletedSeconds', label: 'Mostrar completada (seg.)', type: 'number' },
    { key: 'showCounter', label: 'Mostrar contador general', type: 'bool' },
    {
      key: 'order',
      label: 'Orden',
      type: 'select',
      options: [
        { value: 'recent', label: 'Actividad reciente' },
        { value: 'alphabetical', label: 'Alfabético' },
        { value: 'completed', label: 'Más completadas' },
      ],
    },
    ...SLIDE_CONTROLS,
  ],
  full: [
    {
      key: 'layout',
      label: 'Disposición',
      type: 'select',
      options: [
        { value: 'stacked', label: 'Apilado (timer arriba)' },
        { value: 'split', label: 'Dos columnas' },
        { value: 'sidebar', label: 'Comunidad a la derecha' },
      ],
    },
    { key: 'showTimer', label: 'Mostrar Pomodoro', type: 'bool' },
    { key: 'showFocus', label: 'Mostrar focus de Euge', type: 'bool' },
    { key: 'showTasks', label: 'Mostrar tareas de Euge', type: 'bool' },
    { key: 'showCommunity', label: 'Mostrar comunidad', type: 'bool' },
    { key: 'maxItems', label: 'Máx. tareas', type: 'number' },
    { key: 'maxUsers', label: 'Máx. usuarios', type: 'number' },
    {
      key: 'communityMode',
      label: 'Formato de la comunidad',
      type: 'select',
      options: [
        { value: 'expanded', label: 'Todas las tareas de cada uno' },
        { value: 'compact', label: 'Una línea por persona' },
      ],
    },
    {
      key: 'maxTasksPerUser',
      label: 'Máx. tareas por persona',
      type: 'number',
      hint: 'Solo cuántas se ven acá. El límite de lo que cada viewer puede cargar por chat está en Configuración → Límites.',
    },
    { key: 'showSeconds', label: 'Mostrar segundos', type: 'bool' },
    { key: 'showConfig', label: 'Mostrar configuración (50/10)', type: 'bool' },
    { key: 'showCycle', label: 'Mostrar ciclo', type: 'bool' },
    ...SLIDE_CONTROLS.map((control) => ({
      ...control,
      label: `${control.label} · tareas de Euge`,
    })),
    {
      key: 'communityAutoScroll',
      label: 'Modo slide · comunidad',
      type: 'bool',
      hint: 'Con el slide activo se muestran todas las personas y todas sus tareas.',
    },
    { key: 'communityMaxHeight', label: 'Alto de la comunidad (px)', type: 'number', hint: 'La comunidad nunca pasa de este alto. Si no entra, empieza a desplazarse.' },
    { key: 'communityScrollSpeed', label: 'Velocidad de la comunidad (px/seg.)', type: 'number' },
  ],
};

const SHAPE_OPTIONS: { value: OverlayStyle['panelShape']; label: string }[] = [
  { value: 'rounded', label: 'Redondeada' },
  { value: 'pill', label: 'Cápsula' },
  { value: 'square', label: 'Recta' },
  { value: 'cut', label: 'Esquinas biseladas' },
  { value: 'notch', label: 'Esquina cortada (arriba)' },
  { value: 'leaf', label: 'Hoja (esquinas alternadas)' },
];

const SHADOW_OPTIONS: { value: OverlayStyle['shadowStyle']; label: string }[] = [
  { value: 'soft', label: 'Difusa' },
  { value: 'hard', label: 'Dura (desplazada)' },
  { value: 'none', label: 'Sin sombra' },
];

const ROW_OPTIONS: { value: OverlayStyle['rowStyle']; label: string }[] = [
  { value: 'plain', label: 'Simples' },
  { value: 'striped', label: 'Rayadas' },
  { value: 'card', label: 'Tarjetas' },
  { value: 'underline', label: 'Con renglón' },
];

const MARKER_OPTIONS: { value: OverlayStyle['markerStyle']; label: string }[] = [
  { value: 'dot', label: '● ○ ✓  Puntos' },
  { value: 'check', label: '▸ ☐ ☑  Casillas' },
  { value: 'bracket', label: '[»] [ ] [x]  Corchetes' },
  { value: 'arrow', label: '➜ › ✓  Flechas' },
  { value: 'dash', label: '— – ✓  Guiones' },
  { value: 'square', label: '◼ ◻ ✔  Cuadrados' },
  { value: 'number', label: '1. 2. 3.  Numeradas' },
];

const SOUND_EVENTS: { key: keyof OverlaySound; label: string; hint: string }[] = [
  { key: 'focusEnd', label: 'Fin del pomodoro', hint: 'Suena cuando termina un bloque de focus.' },
  { key: 'breakEnd', label: 'Fin del descanso', hint: 'Suena cuando arranca el focus siguiente.' },
  { key: 'sessionEnd', label: 'Fin de la sesión', hint: 'Suena al completar todos los ciclos.' },
];

const DEFAULT_SOUND: OverlaySound = {
  enabled: false,
  volume: 60,
  focusEnd: 'chime',
  breakEnd: 'marimba',
  sessionEnd: 'bell',
};

const CoworkOverlaysTab: React.FC = () => {
  const [payload, setPayload] = useState<OverlaysPayload | null>(null);
  const [overlay, setOverlay] = useState<CoworkOverlayName>('timer');
  const [draftStyle, setDraftStyle] = useState<OverlayStyle | null>(null);
  const [draftElements, setDraftElements] = useState<OverlayElements>({});
  const [preset, setPreset] = useState('eugenial');
  const [background, setBackground] = useState('scene');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [canvasWidth, setCanvasWidth] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async (keepOverlay?: CoworkOverlayName) => {
    try {
      const { data } = await api.get<OverlaysPayload>('/cowork/overlays');
      setPayload(data);
      const target = keepOverlay || overlay;
      const config = data.configs.find((c) => c.overlay === target);
      if (config) {
        setPreset(config.preset);
        setDraftStyle(config.style);
        setDraftElements(config.elements);
      }
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar los overlays');
    }
  }, [overlay]);

  useEffect(() => {
    load(overlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  useEffect(() => {
    const measure = () => setCanvasWidth(canvasRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [payload]);

  // Preview en vivo: el overlay real recibe el borrador sin necesidad de guardar
  useEffect(() => {
    if (!draftStyle) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'cowork:preview', overlay, style: draftStyle, elements: draftElements },
      '*',
    );
  }, [draftStyle, draftElements, overlay]);

  useEffect(() => {
    const onReady = (event: MessageEvent) => {
      if (event.data?.type !== 'cowork:preview-ready' || !draftStyle) return;
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'cowork:preview', overlay, style: draftStyle, elements: draftElements },
        '*',
      );
    };
    window.addEventListener('message', onReady);
    return () => window.removeEventListener('message', onReady);
  }, [draftStyle, draftElements, overlay]);

  const config: OverlayConfig | undefined = useMemo(
    () => payload?.configs.find((c) => c.overlay === overlay),
    [payload, overlay],
  );

  const basePresetStyle = useMemo(
    () => (payload ? resolvePresetStyle(payload.presets, preset) : {}),
    [payload, preset],
  );

  if (error && !payload) return <ErrorText>{error}</ErrorText>;
  if (!payload || !draftStyle || !config) return <Muted>Cargando overlays…</Muted>;

  const setStyle = (changes: Partial<OverlayStyle>) => setDraftStyle({ ...draftStyle, ...changes });
  const setElement = (key: string, value: any) => setDraftElements({ ...draftElements, [key]: value });

  const sound: OverlaySound = { ...DEFAULT_SOUND, ...(draftStyle.sound || {}) };
  const setSound = (changes: Partial<OverlaySound>) => setStyle({ sound: { ...sound, ...changes } });

  const applyPreset = (presetId: string) => {
    setPreset(presetId);
    const style = resolvePresetStyle(payload.presets, presetId);
    setDraftStyle({ ...draftStyle, ...style } as OverlayStyle);
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put(`/cowork/overlays/${overlay}`, {
        preset,
        style: diffStyle(draftStyle, basePresetStyle),
        replaceStyle: true,
        elements: draftElements,
      });
      await load(overlay);
      setMessage('Overlay guardado. OBS se actualiza solo.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const restore = async () => {
    await api.put(`/cowork/overlays/${overlay}`, {
      preset,
      resetStyle: true,
      elements: payload.defaults[overlay],
    });
    await load(overlay);
    setMessage('Valores del preset restaurados.');
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    await api.post('/cowork/presets', {
      name,
      basePreset: payload.presets.find((p) => p.id === preset)?.builtIn ? preset : 'eugenial',
      style: draftStyle,
    });
    setPresetName('');
    await load(overlay);
    setMessage(`Preset «${name}» guardado.`);
  };

  const deletePreset = async () => {
    const current = payload.presets.find((p) => p.id === preset);
    if (!current || current.builtIn) return;
    await api.delete(`/cowork/presets/${preset}`);
    setPreset('eugenial');
    await load(overlay);
    setMessage('Preset eliminado.');
  };

  const renamePreset = async () => {
    const current = payload.presets.find((p) => p.id === preset);
    if (!current || current.builtIn) return;
    const name = window.prompt('Nuevo nombre del preset', current.name);
    if (!name?.trim()) return;
    await api.put(`/cowork/presets/${preset}`, { name: name.trim() });
    await load(overlay);
  };

  const url = payload.urls[overlay];
  const hint = payload.hints[overlay];
  const scale = canvasWidth > 0 ? canvasWidth / 1920 : 0;
  const previewUrl = `${url}?demo=1`;
  const currentPreset = payload.presets.find((p) => p.id === preset);

  const numberField = (label: string, key: keyof OverlayStyle, min: number, max: number, step = 1) => (
    <Field key={String(key)}>
      <div>
        <Label style={{ margin: 0 }}>{label}</Label>
        <Range
          type="range"
          min={min}
          max={max}
          step={step}
          value={Number(draftStyle[key]) || 0}
          onChange={(e) => setStyle({ [key]: Number(e.target.value) } as any)}
        />
      </div>
      <Input
        type="number"
        value={String(draftStyle[key] ?? 0)}
        onChange={(e) => setStyle({ [key]: Number(e.target.value) } as any)}
      />
    </Field>
  );

  const colorField = (label: string, key: keyof OverlayStyle) => (
    <Field key={String(key)}>
      <Label style={{ margin: 0 }}>{label}</Label>
      <ColorInput
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(String(draftStyle[key])) ? String(draftStyle[key]) : '#ffffff'}
        onChange={(e) => setStyle({ [key]: e.target.value } as any)}
      />
    </Field>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {message && <Toast>{message}</Toast>}
      {error && <ErrorText>{error}</ErrorText>}

      <TabBar>
        {(Object.keys(OVERLAY_LABELS) as CoworkOverlayName[]).map((name) => (
          <TabBtn key={name} $active={overlay === name} onClick={() => setOverlay(name)}>
            {OVERLAY_LABELS[name]}
          </TabBtn>
        ))}
      </TabBar>

      <Editor>
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Apariencia</SurfaceTitle>
            <Button $size="sm" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </SurfaceHeader>
          <SurfaceBody>
            <Controls>
              <Section>
                <SectionTitle>Preset</SectionTitle>
                <Select value={preset} onChange={(e) => applyPreset(e.target.value)}>
                  {payload.presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.builtIn ? '' : ' (propio)'}
                    </option>
                  ))}
                </Select>
                {currentPreset?.description && <Muted>{currentPreset.description}</Muted>}

                <Toolbar style={{ marginTop: '0.5rem' }}>
                  <Button $variant="ghost" $size="sm" onClick={restore}>Restaurar valores</Button>
                  {currentPreset && !currentPreset.builtIn && (
                    <>
                      <Button $variant="ghost" $size="sm" onClick={renamePreset}>Renombrar</Button>
                      <Button $variant="danger" $size="sm" onClick={deletePreset}>Eliminar</Button>
                    </>
                  )}
                </Toolbar>

                <Toolbar style={{ marginTop: '0.5rem' }}>
                  <Input
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Guardar como preset propio"
                  />
                  <Button $variant="ghost" $size="sm" onClick={savePreset}>Guardar</Button>
                </Toolbar>
              </Section>

              <Section>
                <SectionTitle>Colores</SectionTitle>
                {colorField('Acento', 'accentColor')}
                {colorField('Secundario', 'secondaryColor')}
                {colorField('Texto', 'textColor')}
                {colorField('Texto suave', 'mutedColor')}
                {colorField('Fondo', 'backgroundColor')}
                {numberField('Opacidad del fondo', 'backgroundOpacity', 0, 100)}
              </Section>

              <Section>
                <SectionTitle>Tipografía</SectionTitle>
                <Label>Fuente de títulos y reloj</Label>
                <Select
                  value={String(draftStyle.fontFamily)}
                  onChange={(e) => setStyle({ fontFamily: e.target.value })}
                >
                  {payload.fonts.map((font) => (
                    <option key={font.id} value={font.value}>{font.label}</option>
                  ))}
                </Select>
                <Label style={{ marginTop: '0.4rem' }}>Fuente del cuerpo</Label>
                <Select
                  value={String(draftStyle.bodyFontFamily)}
                  onChange={(e) => setStyle({ bodyFontFamily: e.target.value })}
                >
                  {payload.fonts.map((font) => (
                    <option key={font.id} value={font.value}>{font.label}</option>
                  ))}
                </Select>
                {numberField('Tamaño del reloj', 'timerFontSize', 20, 200)}
                {numberField('Tamaño del título', 'titleFontSize', 8, 48)}
                {numberField('Tamaño del texto', 'bodyFontSize', 10, 48)}
                {numberField('Peso', 'fontWeight', 300, 900, 100)}
                {numberField('Espaciado entre letras', 'letterSpacing', 0, 10, 0.5)}
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.uppercaseLabels}
                    onChange={(e) => setStyle({ uppercaseLabels: e.target.checked })}
                  />
                  Títulos en mayúsculas
                </Check>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.textShadow}
                    onChange={(e) => setStyle({ textShadow: e.target.checked })}
                  />
                  Sombra en el texto (legibilidad sin fondo)
                </Check>
              </Section>

              <Section>
                <SectionTitle>Forma</SectionTitle>
                <Label>Forma del panel</Label>
                <Select
                  value={draftStyle.panelShape || 'rounded'}
                  onChange={(e) => setStyle({ panelShape: e.target.value as OverlayStyle['panelShape'] })}
                >
                  {SHAPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
                {numberField('Radio de bordes', 'borderRadius', 0, 48)}
                {numberField('Grosor del borde', 'borderWidth', 0, 8)}
                {colorField('Color del borde', 'borderColor')}

                <Label style={{ marginTop: '0.4rem' }}>Tipo de sombra</Label>
                <Select
                  value={draftStyle.shadowStyle || 'soft'}
                  onChange={(e) => setStyle({ shadowStyle: e.target.value as OverlayStyle['shadowStyle'] })}
                >
                  {SHADOW_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
                {numberField('Sombra', 'shadowStrength', 0, 100)}
                {numberField('Glow', 'glowStrength', 0, 100)}
                {numberField('Desenfoque del fondo', 'backdropBlur', 0, 30)}

                <Label style={{ marginTop: '0.4rem' }}>Filas de la lista</Label>
                <Select
                  value={draftStyle.rowStyle || 'plain'}
                  onChange={(e) => setStyle({ rowStyle: e.target.value as OverlayStyle['rowStyle'] })}
                >
                  {ROW_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>

                <Label style={{ marginTop: '0.4rem' }}>Marcador de tareas</Label>
                <Select
                  value={draftStyle.markerStyle || 'dot'}
                  onChange={(e) => setStyle({ markerStyle: e.target.value as OverlayStyle['markerStyle'] })}
                >
                  {MARKER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>

                <Check style={{ marginTop: '0.45rem' }}>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.centerListItems}
                    onChange={(e) => setStyle({ centerListItems: e.target.checked })}
                  />
                  Centrar también las filas de las listas
                </Check>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.labelDivider}
                    onChange={(e) => setStyle({ labelDivider: e.target.checked })}
                  />
                  Línea debajo de los títulos
                </Check>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.accentBar}
                    onChange={(e) => setStyle({ accentBar: e.target.checked })}
                  />
                  Franja de acento
                </Check>
                {draftStyle.accentBar && (
                  <>
                    <Select
                      value={draftStyle.accentBarPosition || 'left'}
                      onChange={(e) => setStyle({
                        accentBarPosition: e.target.value as OverlayStyle['accentBarPosition'],
                      })}
                    >
                      <option value="left">Franja a la izquierda</option>
                      <option value="top">Franja arriba</option>
                    </Select>
                    {numberField('Grosor de la franja', 'accentBarThickness', 1, 20)}
                  </>
                )}

                {numberField('Espacio a la izquierda del contenido', 'contentIndent', 0, 120)}
                <Muted>
                  Corre todo el contenido hacia la derecha, tengas la franja activada o no.
                </Muted>
              </Section>

              <Section>
                <SectionTitle>Espaciado</SectionTitle>
                {numberField('Padding', 'padding', 0, 60)}
                {numberField('Separación interna', 'gap', 0, 40)}
                {numberField('Tamaño general (%)', 'scale', 40, 200)}
                <Label style={{ marginTop: '0.4rem' }}>Alineación</Label>
                <Select
                  value={draftStyle.align}
                  onChange={(e) => setStyle({ align: e.target.value as OverlayStyle['align'] })}
                >
                  <option value="left">Izquierda</option>
                  <option value="center">Centro</option>
                  <option value="right">Derecha</option>
                </Select>
              </Section>

              <Section>
                <SectionTitle>Posición del reloj</SectionTitle>
                <Label>Alineación del reloj</Label>
                <Select
                  value={draftStyle.timerAlign || 'inherit'}
                  onChange={(e) => setStyle({ timerAlign: e.target.value as OverlayStyle['timerAlign'] })}
                >
                  <option value="inherit">Igual que el panel</option>
                  <option value="left">Izquierda</option>
                  <option value="center">Centro</option>
                  <option value="right">Derecha</option>
                </Select>
                {numberField('Correr en horizontal', 'timerOffsetX', -160, 160)}
                {numberField('Correr en vertical', 'timerOffsetY', -80, 80)}
                <Check style={{ marginTop: '0.35rem' }}>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.timerBoxed}
                    onChange={(e) => setStyle({ timerBoxed: e.target.checked })}
                  />
                  Reloj dentro de su propia caja
                </Check>
                <Muted>Mueve solo el reloj, sin desacomodar el resto del panel.</Muted>
              </Section>

              <Section>
                <SectionTitle>Animaciones</SectionTitle>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.animationsEnabled}
                    onChange={(e) => setStyle({ animationsEnabled: e.target.checked })}
                  />
                  Animaciones activas
                </Check>
                {numberField('Velocidad (%)', 'animationSpeed', 20, 200, 5)}
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.celebrateCompleted}
                    onChange={(e) => setStyle({ celebrateCompleted: e.target.checked })}
                  />
                  Celebrar tareas completadas
                </Check>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!draftStyle.minuteWarning}
                    onChange={(e) => setStyle({ minuteWarning: e.target.checked })}
                  />
                  Aviso visual en el último minuto
                </Check>
              </Section>

              <Section>
                <SectionTitle>Sonido</SectionTitle>
                <Check>
                  <input
                    type="checkbox"
                    checked={!!sound.enabled}
                    onChange={(e) => setSound({ enabled: e.target.checked })}
                  />
                  Este overlay reproduce los sonidos
                </Check>

                <Field style={{ marginTop: '0.35rem' }}>
                  <div>
                    <Label style={{ margin: 0 }}>Volumen</Label>
                    <Range
                      type="range"
                      min={0}
                      max={100}
                      value={sound.volume}
                      onChange={(e) => setSound({ volume: Number(e.target.value) })}
                    />
                  </div>
                  <Input
                    type="number"
                    value={String(sound.volume)}
                    onChange={(e) => setSound({ volume: Number(e.target.value) })}
                  />
                </Field>

                {SOUND_EVENTS.map((event) => (
                  <div key={String(event.key)} style={{ marginTop: '0.5rem' }}>
                    <Label>{event.label}</Label>
                    <Toolbar>
                      <Select
                        value={String(sound[event.key] || 'none')}
                        onChange={(e) => setSound({ [event.key]: e.target.value as SoundId } as Partial<OverlaySound>)}
                      >
                        {payload.sounds.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </Select>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => playCoworkSound(sound[event.key] as SoundId, sound.volume)}
                      >
                        Probar
                      </Button>
                    </Toolbar>
                    <Muted>{event.hint}</Muted>
                  </div>
                ))}

                <Muted style={{ marginTop: '0.5rem' }}>
                  Vienen configurados pero desactivados. Activalos en un solo overlay para que OBS no
                  los duplique.
                </Muted>
              </Section>

              <Section>
                <SectionTitle>Elementos visibles</SectionTitle>
                {ELEMENT_CONTROLS[overlay].map((control) => {
                  if (control.type === 'bool') {
                    return (
                      <div key={control.key}>
                        <Check>
                          <input
                            type="checkbox"
                            checked={draftElements[control.key] !== false}
                            onChange={(e) => setElement(control.key, e.target.checked)}
                          />
                          {control.label}
                        </Check>
                        {control.hint && <Muted>{control.hint}</Muted>}
                      </div>
                    );
                  }
                  if (control.type === 'select') {
                    return (
                      <div key={control.key} style={{ marginTop: '0.35rem' }}>
                        <Label>{control.label}</Label>
                        <Select
                          value={String(draftElements[control.key] ?? control.options?.[0].value)}
                          onChange={(e) => setElement(control.key, e.target.value)}
                        >
                          {control.options?.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Select>
                      </div>
                    );
                  }
                  return (
                    <div key={control.key} style={{ marginTop: '0.35rem' }}>
                      <Field>
                        <Label style={{ margin: 0 }}>{control.label}</Label>
                        <Input
                          type={control.type === 'number' ? 'number' : 'text'}
                          value={String(draftElements[control.key] ?? '')}
                          onChange={(e) => setElement(
                            control.key,
                            control.type === 'number' ? Number(e.target.value) : e.target.value,
                          )}
                        />
                      </Field>
                      {control.hint && <Muted>{control.hint}</Muted>}
                    </div>
                  );
                })}
              </Section>
            </Controls>
          </SurfaceBody>
        </Surface>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Preview · canvas 1920 × 1080</SurfaceTitle>
              <TabBar>
                {[
                  { id: 'scene', label: 'Escena' },
                  { id: 'dark', label: 'Oscuro' },
                  { id: 'light', label: 'Claro' },
                  { id: 'transparent', label: 'Transparente' },
                ].map((option) => (
                  <TabBtn
                    key={option.id}
                    $active={background === option.id}
                    onClick={() => setBackground(option.id)}
                  >
                    {option.label}
                  </TabBtn>
                ))}
              </TabBar>
            </SurfaceHeader>
            <SurfaceBody>
              <Canvas ref={canvasRef} $bg={background}>
                {scale > 0 && (
                  <iframe
                    ref={iframeRef}
                    title={`preview-${overlay}`}
                    src={previewUrl}
                    style={{ transform: `scale(${scale})` }}
                  />
                )}
              </Canvas>
              <Muted>
                La preview es el overlay real con datos de ejemplo. Los cambios se ven al instante,
                pero recién quedan en OBS cuando apretás Guardar.
              </Muted>
            </SurfaceBody>
          </Surface>

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>URL del Browser Source</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              <UrlBox>
                <Input readOnly value={url} onFocus={(e) => e.target.select()} />
                <Button
                  $size="sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(url);
                    setMessage('URL copiada.');
                  }}
                >
                  Copiar URL
                </Button>
              </UrlBox>
              <Muted style={{ marginTop: '0.5rem' }}>
                Resolución sugerida: {hint.width} × {hint.height} · FPS: {hint.fps} · Fondo transparente: sí.
                En OBS dejá «Apagar la fuente cuando no esté visible» sin marcar para que el timer no se corte.
              </Muted>
              <Muted>
                La URL lleva una clave propia del overlay, no tu sesión de admin. Si se filtra, generá una
                nueva desde el botón de abajo y volvé a pegarla en OBS.
              </Muted>
              <Button
                $variant="ghost"
                $size="sm"
                style={{ marginTop: '0.5rem' }}
                onClick={async () => {
                  // eslint-disable-next-line no-restricted-globals
                  if (!window.confirm('Las URLs actuales dejarán de funcionar en OBS. ¿Seguir?')) return;
                  await api.post('/cowork/settings/regenerate-key');
                  await load(overlay);
                  setMessage('Clave regenerada. Actualizá las URLs en OBS.');
                }}
              >
                Regenerar clave de overlays
              </Button>
            </SurfaceBody>
          </Surface>
        </div>
      </Editor>
    </div>
  );
};

export default CoworkOverlaysTab;
